import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Backdrop from '@mui/material/Backdrop';
import { Box, Fade, IconButton, Modal, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Cookies from 'js-cookie';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import Api from '../../Api';
import { profileRequest } from '../../store/actions/users';
import Account from '../../helpers/Account';
import { greenTheme, style } from '../../helpers/style';

const copy = {
    title: ['', 'Extra trial days', 'Дополнительные дни пробного периода', 'Լրացուցիչ փորձարկման օրեր'],
    subtitle: [
        '',
        'Have a promocode? Apply it to get extra free days on your trial.',
        'Есть промокод? Примените его, чтобы получить дополнительные бесплатные дни пробного периода.',
        'Ունե՞ք պրոմոկոդ։ Կիրառեք այն՝ փորձարկման ժամկետին լրացուցիչ անվճար օրեր ավելացնելու համար։',
    ],
    placeholder: ['', 'Enter promocode', 'Введите промокод', 'Մուտքագրեք պրոմոկոդ'],
    apply: ['', 'Apply', 'Применить', 'Կիրառել'],
    later: ['', 'Not now', 'Не сейчас', 'Ոչ հիմա'],
    profileHintBefore: [
        '',
        'You can also apply a promocode from your',
        'Вы также можете применить промокод на странице',
        'Դուք կարող եք պրոմոկոդը կիրառել նաև',
    ],
    profileHintAfter: ['', 'page.', '.', ' էջից։'],
    profileLink: ['', 'profile', 'профиля', 'պրոֆիլի'],
};

export const PROMOCODE_MODAL_DISMISSED_KEY = 'promocode_modal_dismissed';

export function hasAppliedPromocode(user) {
    return Boolean(String(user?.applied_promocode ?? '').trim());
}

/** True when registered → test_period span is exactly 14 days (default trial, not extended). */
export function hasStandard14DayTrial(user) {
    const registered = user?.registered;
    const testPeriod = user?.test_period;
    if (!registered || !testPeriod) {
        return false;
    }
    const days = moment(testPeriod).diff(moment(registered), 'days', true);
    return Math.round(days) === 14;
}

export function shouldShowPromocodeModal(user) {
    if (sessionStorage.getItem(PROMOCODE_MODAL_DISMISSED_KEY) === '1') return false;
    if (!Account.getAdminToken()) return false;
    if (Cookies.get('key')) return false;
    if (Cookies.get('viewer')) return false;
    if (Cookies.get('buddy')) return false;
    if (!user?.id) return false;
    if (hasAppliedPromocode(user)) return false;
    if (+user?.testmode !== 1) return false;
    if (!user?.test_period || !user?.registered) return false;
    if (!hasStandard14DayTrial(user)) return false;
    return true;
}

function PromocodeModal() {
    const dispatch = useDispatch();
    const userData = useSelector((state) => state.users.userData);
    const [open, setOpen] = useState(false);
    const [promocode, setPromocode] = useState('');
    const [loading, setLoading] = useState(false);
    const dismissedRef = useRef(false);

    const primaryColor = process.env.REACT_APP_SERVER === 'ATS' ? '#0973b9' : greenTheme.primary;
    const langIndex = +localStorage.getItem('atsLang') || 1;
    const t = (key) => copy[key][langIndex] || copy[key][1];

    useEffect(() => {
        if (dismissedRef.current) return;

        if (hasAppliedPromocode(userData)) {
            sessionStorage.setItem(PROMOCODE_MODAL_DISMISSED_KEY, '1');
            setOpen(false);
            return;
        }

        if (sessionStorage.getItem(PROMOCODE_MODAL_DISMISSED_KEY) === '1') {
            setOpen(false);
            return;
        }

        if (!userData?.id) {
            return;
        }

        if (shouldShowPromocodeModal(userData)) {
            sessionStorage.setItem(PROMOCODE_MODAL_DISMISSED_KEY, '1');
            setOpen(true);
        } else {
            setOpen(false);
        }
    }, [userData]);

    const handleClose = useCallback(() => {
        dismissedRef.current = true;
        sessionStorage.setItem(PROMOCODE_MODAL_DISMISSED_KEY, '1');
        setOpen(false);
    }, []);

    useEffect(() => {
        if (open && hasAppliedPromocode(userData)) {
            dismissedRef.current = true;
            sessionStorage.setItem(PROMOCODE_MODAL_DISMISSED_KEY, '1');
            setOpen(false);
        }
    }, [userData, open]);

    const handleApply = useCallback(async () => {
        const code = promocode.trim();
        if (!code) {
            toast.error(t('placeholder'));
            return;
        }
        try {
            setLoading(true);
            const { data } = await Api.applyPromocode(code);
            const daysAdded = data?.days_added;
            toast.success(
                daysAdded
                    ? `Promocode applied: +${daysAdded} day${daysAdded === 1 ? '' : 's'} added to your trial`
                    : 'Promocode applied'
            );
            setPromocode('');
            dismissedRef.current = true;
            sessionStorage.setItem(PROMOCODE_MODAL_DISMISSED_KEY, '1');
            setOpen(false);
            await dispatch(profileRequest());
        } catch (e) {
            toast.error(e?.response?.data?.message || 'Failed to apply promocode');
        } finally {
            setLoading(false);
        }
    }, [promocode, dispatch, langIndex]);

    return (
        <Modal
            aria-labelledby="promocode-modal-title"
            aria-describedby="promocode-modal-description"
            open={open}
            onClose={handleClose}
            closeAfterTransition
            slots={{ backdrop: Backdrop }}
            slotProps={{
                backdrop: {
                    timeout: 500,
                },
            }}
        >
            <Fade in={open}>
                <Box sx={{ ...style, position: 'relative', borderRadius: '12px', overflow: 'visible', width: window.innerWidth > 500 ? 480 : window.innerWidth }}>
                    <IconButton
                        onClick={handleClose}
                        aria-label="Close"
                        sx={{
                            position: 'absolute',
                            right: -56,
                            top: 24,
                            zIndex: 1,
                            width: 56,
                            height: 40,
                            borderRadius: '0 12px 12px 0',
                            backgroundColor: primaryColor,
                            color: '#fff',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            '&:hover': { backgroundColor: primaryColor, opacity: 0.9, color: '#fff' },
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                    <div className="d-flex flex-row justify-content-between">
                        <Typography id="promocode-modal-title" variant="h6" component="h2">
                            {t('title')}
                        </Typography>
                    </div>
                    <Box
                        id="promocode-modal-description"
                        sx={{
                            mt: 2,
                            pr: 1,
                        }}
                    >
                        <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.5 }}>
                            {t('subtitle')}
                        </Typography>
                        <Typography variant="body2" component="p" sx={{ mb: 2, lineHeight: 1.5, color: 'text.secondary', fontSize: 13 }}>
                            {t('profileHintBefore')}{' '}
                            <Link
                                to="/profile"
                                onClick={handleClose}
                                style={{ color: primaryColor, fontWeight: 600 }}
                            >
                                {t('profileLink')}
                            </Link>{' '}
                            {t('profileHintAfter')}
                        </Typography>
                        <input
                            className="create_input"
                            value={promocode}
                            onChange={(ev) => setPromocode(ev.target.value.toUpperCase())}
                            placeholder={t('placeholder')}
                            autoFocus
                            style={{ width: '100%', marginBottom: 20 }}
                        />
                        <div className="d-flex justify-content-center flex-wrap" style={{ gap: 12 }}>
                            <button type="button" className="addBtn" onClick={handleApply} disabled={loading}>
                                {loading ? '...' : t('apply')}
                            </button>
                            <button
                                type="button"
                                className="addBtn"
                                style={{ background: '#6c757d' }}
                                onClick={handleClose}
                                disabled={loading}
                            >
                                {t('later')}
                            </button>
                        </div>
                    </Box>
                </Box>
            </Fade>
        </Modal>
    );
}

export default PromocodeModal;
